import api from '../../../shared/utils/api';
import type { Prospect, PaginatedResponse } from '../../../shared/types';
import type { EnriquecimentoDaPromocao } from '../lib/promocaoEnriquecimento';

export interface ConvertToDealResponse {
  deal: { id: string };
  /** EP-8 / WP-8.1 — aditivo: ausente num BFF anterior. */
  enriquecimento?: EnriquecimentoDaPromocao;
}

export interface CreateProspectPayload {
  nomeEmpresa: string;
  razaoSocial?: string;
  segmento?: string;
  origemLead?: string;
  website?: string;
  observacoes?: string;
  cnpj?: string;
  cnae?: string;
  nomeFantasia?: string;
  porte?: string;
  emailEmpresa?: string;
  telefone?: string;
  dataAbertura?: string;
  capitalSocial?: string;
  naturezaJuridica?: string;
  situacaoCadastral?: string;
  tipoEmpresa?: string;
  logradouro?: string;
  numeroEndereco?: string;
  bairro?: string;
  cep?: string;
  complementoEndereco?: string;
  uf?: string;
  simplesOptante?: boolean;
  simeiOptante?: boolean;
  qsa?: Array<{ nome: string; qual: string }>;
  atividadesSecundarias?: Array<{ code: string; text: string }>;
  localizacao?: string;
  funcionarios?: string;
  faturamento?: string;
  linkedinUrl?: string;
  decisor?: string;
  scoreIa?: number;
  veredictoIa?: string;
}

/**
 * Response from `GET /prospects/:id/business-context`.
 *
 * Backs the "Contexto Empresarial" tab on the prospect detail page. When the
 * tenant has no ICP configured, `hasIcp` is `false` and `icpScore` is 0 — the
 * frontend then renders a fallback CTA linking to `/settings/icp` instead of
 * the score card.
 */
export interface BusinessContextResponse {
  /**
   * Generation status for the cached business context + ICP fit rows.
   * - `ready`: both universal context and (when ICP configured) tenant ICP
   *   score are cached — render the full tab.
   * - `generating`: at least one row is still being generated in the
   *   background; the frontend should poll until status flips to `ready`.
   * - `sem_cnpj`: TERMINAL — the company has no resolved CNPJ, so the
   *   background orchestrator (keyed by CNPJ end-to-end) can never run.
   *   Never poll on this status; show the real reason instead of a spinner
   *   that would never resolve (achado 2026-08-26, caso Dextra Labs).
   */
  status?: 'ready' | 'generating' | 'sem_cnpj';
  companyName: string;
  summary: string;
  sectorContext: string;
  website?: string | null;
  linkedinUrl?: string | null;
  segmento?: string | null;
  employeeCount?: string | null;
  annualRevenue?: string | null;
  techStack: string[];
  opportunities: string[];
  challenges: string[];
  hasIcp: boolean;
  icpError?: string | null;
  /**
   * Scoring status from machine-ai:
   * - `"ok"`: score was computed successfully
   * - `"not_configured"`: tenant has no ICP configured
   * - `"unavailable"`: LLM call failed or returned an unusable response.
   *   Treat as "couldn't score" — don't render the score as a real verdict.
   */
  icpStatus?: string | null;
  icpScore: number;
  icpRationale: string;
  icpStrengths: string[];
  icpWeaknesses: string[];
  estimatedIcpScore?: number | null;
  estimatedIcpFactors?: string[] | null;
}

export interface EnrichProspectRequest {
  // BFF accepts either companyName OR cnpj (or both). The caller must
  // supply at least one; enforced server-side with a 400 response.
  companyName?: string;
  cnpj?: string;
  location?: string;
  sector?: string;
}

export interface EnrichProspectResponse {
  companyName: string;
  razaoSocial?: string;
  cnpj?: string;
  cnae?: string;
  nomeFantasia?: string;
  porte?: string;
  emailEmpresa?: string;
  telefone?: string;
  dataAbertura?: string;
  capitalSocial?: string;
  naturezaJuridica?: string;
  situacaoCadastral?: string;
  tipoEmpresa?: string;
  logradouro?: string;
  numeroEndereco?: string;
  bairro?: string;
  cep?: string;
  complementoEndereco?: string;
  uf?: string;
  simplesOptante?: boolean;
  simeiOptante?: boolean;
  qsa?: Array<{ nome: string; qual: string }>;
  atividadesSecundarias?: Array<{ code: string; text: string }>;
  segmento?: string;
  localizacao?: string;
  resumoEmpresa?: string;
  // Fields enriched via Vertex AI Search + company_context prompt
  website?: string;
  linkedinUrl?: string;
  funcionarios?: string;
  faturamento?: string;
  techStack?: string;
  cached?: boolean;
}

export interface UpdateProspectPayload extends Partial<CreateProspectPayload> {
  veredicto?: 'pendente' | 'em_analise' | 'aprovado' | 'reprovado';
  /** Rejection reason — maps to sales-api ``motivo_recusa`` (toSnakeCase in BFF). */
  motivoRecusa?: string;
}

export interface TransferPayload {
  toUserId: number;
  reason?: string;
}

const CONTEXT_CACHE_PREFIX = 'bctx:';
const CONTEXT_CACHE_TTL_MS = 10 * 60 * 1000;

function getContextFromSession(prospectId: string): BusinessContextResponse | null {
  try {
    const raw = sessionStorage.getItem(CONTEXT_CACHE_PREFIX + prospectId);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: BusinessContextResponse; ts: number };
    if (Date.now() - ts > CONTEXT_CACHE_TTL_MS) {
      sessionStorage.removeItem(CONTEXT_CACHE_PREFIX + prospectId);
      return null;
    }
    if (data.status !== 'ready') return null;
    return data;
  } catch {
    return null;
  }
}

function setContextToSession(prospectId: string, data: BusinessContextResponse): void {
  if (data.status !== 'ready') return;
  try {
    sessionStorage.setItem(
      CONTEXT_CACHE_PREFIX + prospectId,
      JSON.stringify({ data, ts: Date.now() }),
    );
  } catch {
    /* quota exceeded — ignore */
  }
}

export const prospectsApi = {
  /**
   * Lista leads. Os quatro filtros abaixo são TODOS os que a sales-api
   * aplica hoje (`prospect_service.list_prospects`) — medido nos três hops
   * em 2026-09-06. Até então este cliente declarava só três, e a tela usava
   * um: `veredicto` e `owner_user_id` existiam no backend e no BFF e eram
   * INALCANÇÁVEIS da tela. Um cliente que declara menos do que o backend
   * aceita não é a fonte de verdade — é o que esconde a capacidade.
   */
  list: async (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    veredicto?: string;
    owner_user_id?: number;
    /** Sentinela do balde sem dono: `'unassigned'` ⇒ `owner_user_id IS NULL`. */
    owner?: 'unassigned';
    segmento?: string;
    faturamento?: string;
    faturamento_min?: number;
    faturamento_max?: number;
    funcionarios?: string;
    funcionarios_min?: number;
    funcionarios_max?: number;
    uf?: string;
    porte?: string;
    cnae?: string;
  }): Promise<PaginatedResponse<Prospect>> => {
    const { data } = await api.get<PaginatedResponse<Prospect>>('/prospects', {
      params,
    });
    return data;
  },

  getById: async (id: string): Promise<Prospect> => {
    const { data } = await api.get<Prospect>(`/prospects/${id}`);
    return data;
  },

  /**
   * Find a prospect in the current tenant by CNPJ. Resolves to `null` on 404
   * so callers can fall back to a non-prospect UI without try/catch.
   */
  findByCnpj: async (cnpj: string): Promise<Prospect | null> => {
    try {
      const { data } = await api.get<Prospect>('/prospects/by-cnpj', { params: { cnpj } });
      return data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  /**
   * Create a new prospect. If a prospect with the same CNPJ already exists
   * in the current tenant, the BFF returns the existing record (HTTP 200)
   * with `alreadyExists: true`. Callers should check that flag to decide
   * whether to show a "prospect created" or "already exists" UX.
   */
  create: async (payload: CreateProspectPayload): Promise<Prospect & { alreadyExists?: boolean }> => {
    const { data } = await api.post<Prospect & { alreadyExists?: boolean }>('/prospects', payload);
    return data;
  },

  update: async (id: string, payload: UpdateProspectPayload): Promise<Prospect> => {
    const { data } = await api.patch<Prospect>(`/prospects/${id}`, payload);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/prospects/${id}`);
  },

  /** Claim an unassigned prospect — assigns it to the current user. */
  claim: async (id: string): Promise<Prospect> => {
    const { data } = await api.post<Prospect>(`/prospects/${id}/claim`, {});
    return data;
  },

  /** Transfer a prospect to another user (managers only). */
  transfer: async (id: string, payload: TransferPayload): Promise<Prospect> => {
    const { data } = await api.post<Prospect>(`/prospects/${id}/transfer`, payload);
    return data;
  },

  /** Convert a prospect into a deal in the pipeline. */
  /**
   * EP-8 / WP-8.1 — a resposta carrega `enriquecimento`, o desfecho do ramo
   * síncrono (19–34 s) que roda quando o lead não tem razão social nem CNPJ.
   * Campo ADITIVO e opcional de propósito: um BFF anterior não o manda, e a
   * tela trata a ausência como silêncio, nunca como falha.
   */
  convertToDeal: async (id: string): Promise<ConvertToDealResponse> => {
    const { data } = await api.post<ConvertToDealResponse>(`/prospects/${id}/convert-to-deal`, {});
    return data;
  },

  /** Enrich company data via machine-ai pipeline (ReceitaWS + LLM). */
  enrich: async (payload: EnrichProspectRequest): Promise<EnrichProspectResponse> => {
    const { data } = await api.post<EnrichProspectResponse>('/prospects/enrich', payload, { timeout: 120_000 });
    return data;
  },

  /**
   * Business context + ICP fit for the "Contexto Empresarial" prospect tab.
   * The BFF caches the response for 1h per prospect id.
   */
  getBusinessContext: async (id: string, force?: boolean): Promise<BusinessContextResponse> => {
    if (!force) {
      const cached = getContextFromSession(id);
      if (cached) return cached;
    }
    const params = force ? { force: 'true' } : undefined;
    const { data } = await api.get<BusinessContextResponse>(`/prospects/${id}/business-context`, { params });
    setContextToSession(id, data);
    return data;
  },

  /**
   * Fire-and-forget: notifies the BFF that a user has opened a prospect row.
   * The BFF orchestrates background population of the universal company
   * context, tenant-cross ICP fit, and shared contact_core pool. Returns 202
   * immediately; UI should NOT await this before rendering the drawer.
   *
   * @param language - User's preferred language (e.g. 'pt-BR', 'en') for
   *   business context generation. Passed to machine-ai prompts.
   */
  open: async (id: string, language?: string): Promise<void> => {
    await api.post(`/prospects/${id}/open`, { language: language || 'pt-BR' });
  },

  /** Dispara a promoção assíncrona em lote com fila no backend. */
  startBulkPromote: async (companies: Array<Record<string, unknown>>): Promise<BulkPromoteStartResponse> => {
    const { data } = await api.post<BulkPromoteStartResponse>('/prospects/bulk-promote', { companies });
    return data;
  },

  /** Consulta o status e progresso do lote de promoção assíncrona. */
  getBulkPromoteStatus: async (jobId: string): Promise<BulkPromoteJobStatusResponse> => {
    const { data } = await api.get<BulkPromoteJobStatusResponse>(`/prospects/bulk-promote-jobs/${jobId}`);
    return data;
  },
};

export interface BulkPromoteJobItemResult {
  cnpj?: string;
  dealId?: string;
  companyName: string;
  status: 'success' | 'failed';
  website?: string;
  linkedinUrl?: string;
  contactsCount: number;
  error?: string;
}

export interface BulkPromoteJobStatusResponse {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  totalItems: number;
  processedItems: number;
  successItems: number;
  failedItems: number;
  itemsResult: BulkPromoteJobItemResult[];
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface BulkPromoteStartResponse {
  jobId: string;
  status: string;
  totalItems: number;
}
